import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Chip,
  InputAdornment,
  Grid,
  MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Delete, Search, Visibility, Download } from '@mui/icons-material';
import { applicationAPI, jobAPI, API_BASE_URL } from '../services/api';

const STATUSES = ['pending', 'reviewed', 'shortlisted', 'rejected', 'hired'];
const STATUS_COLORS = {
  pending: 'default',
  reviewed: 'info',
  shortlisted: 'warning',
  rejected: 'error',
  hired: 'success'
};

const Applications = () => {
  const [applications, setApplications] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [currentApplication, setCurrentApplication] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    jobId: '',
    status: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, [filters]);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const params = { limit: 100, ...filters };
      const response = await applicationAPI.getAll(params);
      setApplications(response.data.applications || []);
    } catch (error) {
      showSnackbar('Failed to fetch applications', 'error');
    }
    setLoading(false);
  };

  const fetchJobs = async () => {
    try {
      const response = await jobAPI.getAll({ limit: 100 });
      setJobs(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch jobs');
    }
  };

  const handleViewApplication = (application) => {
    setCurrentApplication(application);
    setOpenViewDialog(true);
  };

  const handleStatusChange = async (status) => {
    try {
      await applicationAPI.updateStatus(currentApplication._id, status);
      setCurrentApplication({ ...currentApplication, status });
      showSnackbar('Status updated successfully', 'success');
      fetchApplications();
    } catch (error) {
      showSnackbar('Failed to update status', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        await applicationAPI.delete(id);
        showSnackbar('Application deleted successfully', 'success');
        fetchApplications();
      } catch (error) {
        showSnackbar('Delete failed', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredApplications = applications.filter((app) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      app.fullName?.toLowerCase().includes(term) ||
      app.email?.toLowerCase().includes(term) ||
      app.phone?.toLowerCase().includes(term) ||
      app.job?.title?.toLowerCase().includes(term)
    );
  });

  const columns = [
    {
      field: 'fullName',
      headerName: 'Candidate',
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{params.value}</Typography>
          <Typography variant="caption" color="textSecondary">{params.row.email}</Typography>
        </Box>
      )
    },
    {
      field: 'phone',
      headerName: 'Phone',
      width: 140
    },
    {
      field: 'job',
      headerName: 'Job',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.row.job?.title || 'N/A'}</Typography>
          <Typography variant="caption" color="textSecondary">
            {params.row.job?.company?.name || params.row.job?.entity || ''}
          </Typography>
        </Box>
      )
    },
    {
      field: 'appliedAt',
      headerName: 'Applied On',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric'
          }) : 'N/A'}
        </Typography>
      )
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={STATUS_COLORS[params.value] || 'default'}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      )
    },
    {
      field: 'resume',
      headerName: 'Resume',
      width: 110,
      sortable: false,
      renderCell: (params) => (
        params.row.resumeUrl ? (
          <IconButton
            size="small"
            color="primary"
            component="a"
            href={`${API_BASE_URL}/${params.row.resumeUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            title={params.row.resumeFileName || 'Download resume'}
          >
            <Download />
          </IconButton>
        ) : (
          <Typography variant="caption" color="textSecondary">N/A</Typography>
        )
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            color="info"
            onClick={() => handleViewApplication(params.row)}
          >
            <Visibility />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => handleDelete(params.row._id)}
          >
            <Delete />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Applications</Typography>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by name, email, phone or job..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Job"
              value={filters.jobId}
              onChange={(e) => setFilters({ ...filters, jobId: e.target.value })}
            >
              <MenuItem value="">All Jobs</MenuItem>
              {jobs.map((job) => (
                <MenuItem key={job._id} value={job._id}>
                  {job.title}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((status) => (
                <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={filteredApplications}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          loading={loading}
          getRowId={(row) => row._id}
          disableSelectionOnClick
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-cell': { py: 1 }
          }}
        />
      </Paper>

      {/* View Application Dialog */}
      <Dialog open={openViewDialog} onClose={() => setOpenViewDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{currentApplication?.fullName}</DialogTitle>
        <DialogContent>
          {currentApplication && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">
                    <a href={`mailto:${currentApplication.email}`}>{currentApplication.email}</a>
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Phone</Typography>
                  <Typography variant="body1">
                    <a href={`tel:${currentApplication.phone}`}>{currentApplication.phone}</a>
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Job</Typography>
                  <Typography variant="body1">{currentApplication.job?.title || 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Applied On</Typography>
                  <Typography variant="body1">
                    {new Date(currentApplication.appliedAt).toLocaleString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <TextField
                    select
                    size="small"
                    value={currentApplication.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    sx={{ mt: 0.5, minWidth: 160 }}
                  >
                    {STATUSES.map((status) => (
                      <MenuItem key={status} value={status} sx={{ textTransform: 'capitalize' }}>
                        {status}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Resume</Typography>
                  {currentApplication.resumeUrl ? (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Download />}
                      component="a"
                      href={`${API_BASE_URL}/${currentApplication.resumeUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ mt: 0.5 }}
                    >
                      {currentApplication.resumeFileName || 'Download'}
                    </Button>
                  ) : (
                    <Typography variant="body1" color="textSecondary">Not attached</Typography>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    Cover Letter
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {currentApplication.coverLetter || 'N/A'}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Applications;
